import { combineReducers, createStore, applyMiddleware, compose } from 'redux'
import { persistStore, persistReducer } from 'redux-persist'
import { createLogger } from 'redux-logger'
import createSagaMiddleware, { END } from 'redux-saga'
import sagaMonitor from '@redux-saga/simple-saga-monitor'
import thunkMiddleware from 'redux-thunk'
import storage from 'redux-persist/lib/storage'
import GoogleTagManager from '@redux-beacon/google-tag-manager'
import { createMiddleware } from 'redux-beacon'
import { GTMEventsMap } from '@/redux/middlewares'

import { COLORS } from '@/constants/ui.constants'
import { isProduction } from '@/utils'
import { isFailureAction, isRequestAction, isSuccessAction } from '@/utils/actions.utils'
import { notAuthorized, retryDispatch } from '@/redux/middlewares'
import reducers from '@/redux/reducers'

const gtm = GoogleTagManager()

const sagaMiddleware = createSagaMiddleware({ sagaMonitor })

const gtmMiddleware = createMiddleware(GTMEventsMap, gtm)

const middlewares = [
	gtmMiddleware,
	notAuthorized,
	retryDispatch,
	thunkMiddleware,
	sagaMiddleware,
	thunkMiddleware,
	!isProduction() &&
	createLogger({
		collapsed: true,
		colors: {
			title: (action) => {
				if (isRequestAction(action.type)) {
					return COLORS.gray2
				}

				if (isSuccessAction(action.type)) {
					return COLORS.success
				}

				if (isFailureAction(action.type) || action.type === 'SET_ERROR') {
					return COLORS.error
				}

				return COLORS.black
			}
		}
	})
].filter(Boolean)

const composeEnhancers = process.browser ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose : compose

const rootReducer = combineReducers({
	...Object.values(reducers.standardReducers).reduce((acc, reducer) => {
		if (reducer.reducers) {
			return {
				...acc,
				[reducer.name]: combineReducers({ ...reducer.reducers })
			}
		}

		if (reducer.onlyReducer) {
			return {
				...acc,
				[reducer.name]: reducer.onlyReducer
			}
		}

		return acc
	}, {}),
	...Object.values(reducers.persistedReducers).reduce((acc, reducer) => {
		if (reducer.reducers) {
			return {
				...acc,
				[reducer.name]: persistReducer({ key: [reducer.name], storage }, combineReducers({ ...reducer.reducers }))
			}
		}

		if (reducer.onlyReducer) {
			return {
				...acc,
				[reducer.name]: persistReducer({ key: [reducer.name], storage }, reducer.onlyReducer)
			}
		}

		return acc
	}, {})
})

export const initializeStore = () => {
	const store = createStore(rootReducer, composeEnhancers(applyMiddleware(...middlewares)))
	store.runSaga = sagaMiddleware.run
	store.close = () => store.dispatch(END)
	const persistor = persistStore(store)
	return { store, persistor }
}
